import { yupResolver } from '@hookform/resolvers/yup';
import { Link as MaterialLink } from '@mui/material';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import * as yup from 'yup';
import {
	ENDORSEMENT_POLICY_END_DATE_ENABLED,
	SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED,
	useAuthContext,
} from '../../../context/auth';
import { useEndorsementContext } from '../../../context/EndorsementProvider';
import { ENDORSEMENT_NEW_POLICY_END_DATE_WARNING_MSG } from '../../../data/labelsAndMessages';
import { PolicyCharacteristics } from '../../../types/policy';
import { getActiveCharacteristics } from '../../../util/util';
import { FastForm } from '../../fastForm';
import {
	DATE_FIELD_VALIDATIONS,
	DATE_PRECISION,
	DATE_PRECISION_BACKEND_FORMAT,
	DATE_PRECISION_INPUT_FORMAT,
	FORM_MESSAGE,
	FORM_NAME,
} from '../../fastForm/data/constants';
import { DateField } from '../../fastForm/fields';
import { dateConversion } from '../../fastForm/utils';
import FieldLabel from '../../fields/FieldLabel';
import ReactSelect, { SelectValue } from '../fields/ReactSelect';

interface Props {
	premiumReportEndDates: string[];
}

interface FormValues {
	effectiveDate: string;
	newPolicyEndDate: string;
}

const useStyles = makeStyles((theme) => ({
	grid: {
		paddingTop: '10px',
		paddingRight: '10px',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
	},
	linkWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginBottom: theme.spacing(1),
	},
}));

/**
 * The policy detail page for an endorsement.
 */
const PolicyDetails = (props: Props) => {
	const { premiumReportEndDates } = props;
	const classes = useStyles();
	const { t } = useTranslation();
	const authData = useAuthContext();
	const endorsementData = useEndorsementContext();
	const timezone = authData.auth.timezone;
	const isSecondPrecisionEnabled =
		authData.auth.configProperties[
			SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED
		];
	const isEndorsementEndDateEnabled =
		authData.auth.configProperties[ENDORSEMENT_POLICY_END_DATE_ENABLED];
	const DATE_PICKER_PRECISION = isSecondPrecisionEnabled
		? DATE_PRECISION.SECOND
		: DATE_PRECISION.DAY;
	const { hasPermission } = authData;
	const {
		endorsementUpdateObj,
		setEndorsementUpdateObj,
		productConfig,
		policy,
		setLoading,
		fields: form,
		saveFastForm,
	} = endorsementData;

	const { policyConfiguration } = productConfig;

	const { effectiveContractEndTimestamp } = policy;
	const currentEndDate = dateConversion
		.convertTimestampToDayJS(effectiveContractEndTimestamp, timezone)
		.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]);
	const helperText = `Current end date: ${currentEndDate}`;

	const endorsementEffectiveTimestamp = endorsementUpdateObj.startTimestamp;
	const formattedEffectiveTimestamp = dateConversion
		.convertTimestampToDayJS(endorsementEffectiveTimestamp, timezone)
		.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]);
	const newPolicyEndTimestamp = endorsementUpdateObj.newPolicyEndTimestamp;
	const formattedNewPolicyEndTimestamp = dateConversion
		.convertTimestampToDayJS(newPolicyEndTimestamp, timezone)
		.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]);
	const paymentScheduleName =
		endorsementUpdateObj.newPaymentScheduleName || policy.paymentScheduleName;
	const repriceBool = endorsementUpdateObj.reprice;
	const { policyStartTimestamp } = (
		getActiveCharacteristics(policy.characteristics) as PolicyCharacteristics[]
	)[0];

	const minimumEffectiveDate = dateConversion.convertTimestampToDayJS(
		premiumReportEndDates.length > 0
			? premiumReportEndDates.sort().pop()
			: policyStartTimestamp,
		timezone,
	);

	const minimumEffectiveDateString = minimumEffectiveDate.format(
		DATE_PRECISION_BACKEND_FORMAT[DATE_PICKER_PRECISION],
	);

	const maximumEffectiveDate = dateConversion.convertTimestampToDayJS(
		effectiveContractEndTimestamp,
		timezone,
	);

	const maximumEffectiveDateString = maximumEffectiveDate.format(
		DATE_PRECISION_BACKEND_FORMAT[DATE_PICKER_PRECISION],
	);

	const minimumEffectiveDateInputString = minimumEffectiveDate.format(
		DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION],
	);

	const maximumEffectiveDateInputString = maximumEffectiveDate.format(
		DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION],
	);

	const minimumEffectiveDateErrorString =
		DATE_FIELD_VALIDATIONS.MIN_DATE_ERROR_MESSAGE +
		minimumEffectiveDateInputString;

	const maximumEffectiveDateErrorString =
		DATE_FIELD_VALIDATIONS.MAX_DATE_ERROR_MESSAGE +
		maximumEffectiveDateInputString;

	const invalidDateErrorString =
		DATE_FIELD_VALIDATIONS.INVALID_DATE_ERROR_MESSAGE;

	const { control, watch } = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: {
			effectiveDate: dateConversion
				.convertTimestampToDayJS(endorsementEffectiveTimestamp, timezone)
				.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]),
			newPolicyEndDate: !newPolicyEndTimestamp
				? null
				: dateConversion
						.convertTimestampToDayJS(newPolicyEndTimestamp, timezone)
						.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]),
		},
		resolver: yupResolver(
			yup.object().shape({
				effectiveDate: yup
					.string()
					.required(FORM_MESSAGE.required)
					.test('is-valid-date', invalidDateErrorString, (value) => {
						const date = dateConversion.dateFormStringToDayJs(
							value,
							DATE_PICKER_PRECISION,
						);
						return date?.isValid();
					})
					.test(
						'is-after-minimum-date',
						minimumEffectiveDateErrorString,
						(value) => {
							const currentDate =
								dateConversion.convertDateStringToZonedTimestamp(
									value,
									DATE_PICKER_PRECISION,
									timezone,
								);
							const minimumDate =
								dateConversion.convertDateStringToZonedTimestamp(
									minimumEffectiveDateInputString,
									DATE_PICKER_PRECISION,
									timezone,
								);
							return parseInt(currentDate) >= parseInt(minimumDate);
						},
					)
					.test(
						'is-before-maximum-date',
						maximumEffectiveDateErrorString,
						(value) => {
							const currentDate =
								dateConversion.convertDateStringToZonedTimestamp(
									value,
									DATE_PICKER_PRECISION,
									timezone,
								);
							const maximumDate =
								dateConversion.convertDateStringToZonedTimestamp(
									maximumEffectiveDateInputString,
									DATE_PICKER_PRECISION,
									timezone,
								);
							return parseInt(currentDate) <= parseInt(maximumDate);
						},
					),
				newPolicyEndDate: yup
					.string()
					.test('is-valid-date', invalidDateErrorString, (value) => {
						if (value === '') {
							return true;
						}
						const date = dateConversion.dateFormStringToDayJs(
							value,
							DATE_PICKER_PRECISION,
						);
						return date?.isValid();
					})
					.test(
						'is-at-midnight',
						'New Policy End Date must be at exactly midnight',
						(value) => {
							if (value === '') {
								return true;
							}
							const date = dateConversion.dateFormStringToDayJs(
								value,
								DATE_PICKER_PRECISION,
							);
							if (
								date.hour() === 0 &&
								date.minute() === 0 &&
								date.second() === 0
							) {
								return true;
							}
							return false;
						},
					)
					.when('effectiveDate', (effectiveDate: string, schema: any) => {
						return schema.test(
							'is-after-effective-date',
							`New Policy End Date must be after ${
								effectiveDate ? effectiveDate : 'effective date'
							}`,
							(value: string) => {
								if (value === '') {
									return true;
								}
								const effectiveDateObject =
									dateConversion.dateFormStringToDayJs(
										effectiveDate,
										DATE_PICKER_PRECISION,
									);
								const newPolicyEndDateObject =
									dateConversion.dateFormStringToDayJs(
										value,
										DATE_PICKER_PRECISION,
									);
								return newPolicyEndDateObject > effectiveDateObject;
							},
						);
					}),
			}),
		),
	});

	const currEffectiveDateTimeString = watch('effectiveDate');
	const currNewPolicyEndDate = watch('newPolicyEndDate');

	React.useEffect(() => {
		const formatEffectiveDate = () => {
			const newStartTimestampString =
				dateConversion.convertDateStringToZonedTimestamp(
					currEffectiveDateTimeString,
					DATE_PICKER_PRECISION,
					timezone,
				);
			endorsementUpdateObj.startTimestamp = newStartTimestampString;
			setEndorsementUpdateObj(endorsementUpdateObj);
		};
		// fix for SOCA-9523 only convert string if date has been changed
		if (currEffectiveDateTimeString !== formattedEffectiveTimestamp) {
			formatEffectiveDate();
		}
	}, [currEffectiveDateTimeString]);

	React.useEffect(() => {
		const formatNewPolicyEndDate = () => {
			const newEndTimestampString =
				dateConversion.convertDateStringToZonedTimestamp(
					currNewPolicyEndDate,
					DATE_PICKER_PRECISION,
					timezone,
				);
			if (isEndorsementEndDateEnabled) {
				endorsementUpdateObj.newPolicyEndTimestamp = newEndTimestampString;
				setEndorsementUpdateObj(endorsementUpdateObj);
			}
		};
		// fix for SOCA-9523 only convert string if date has been changed
		if (currNewPolicyEndDate !== formattedNewPolicyEndTimestamp) {
			formatNewPolicyEndDate();
		}
	}, [currNewPolicyEndDate]);

	const getPaymentScheduleOptions = () => {
		if (!productConfig) {
			return null;
		}

		const { paymentSchedules } = policyConfiguration;

		return paymentSchedules.map(
			(paymentSchedule) =>
				({
					value: paymentSchedule.name,
					label: paymentSchedule.displayName,
				} as any),
		);
	};

	const getRepriceBehavior = () => {
		if (!productConfig) {
			return null;
		}

		const { endorsements } = policyConfiguration;
		const endorsementConfig = endorsements.find(
			(e) => e.name === endorsementUpdateObj.endorsementName,
		);
		if (!endorsementConfig) {
			return null;
		}
		return endorsementConfig.reprice;
	};
	const paymentScheduleOptions = getPaymentScheduleOptions();
	const defaultPaymentSchedule = paymentScheduleOptions.find(
		(p) => p.value === paymentScheduleName,
	) || { value: paymentScheduleName, label: paymentScheduleName };

	const repriceBehavior = getRepriceBehavior();

	if (!paymentScheduleOptions) {
		return null;
	}

	const isEditable =
		hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';

	const updatePaymentSchedule = (option: SelectValue) => {
		const paymentSchedule = option.value;
		endorsementUpdateObj.newPaymentScheduleName = paymentSchedule;
		setEndorsementUpdateObj(endorsementUpdateObj);
	};

	const updateReprice = (option: SelectValue) => {
		const repriceVal = option.value === 'true' ? true : false;
		endorsementUpdateObj.reprice = repriceVal;
		setEndorsementUpdateObj(endorsementUpdateObj);
	};

	const hasPaymentPlanPluginEnabled =
		!!policyConfiguration?.plugins?.createInstallments?.enabled;

	const currEffectiveDayJsObj = dateConversion.dateFormStringToDayJs(
		currEffectiveDateTimeString,
		DATE_PICKER_PRECISION,
	);

	return (
		<Grid container spacing={2}>
			<Grid item container xs={12} justifyContent='flex-end'>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink component={RouterLink} to='../auxData'>
						Aux Data
					</MaterialLink>
				)}
			</Grid>
			{!!newPolicyEndTimestamp && (
				<Grid item xs={12}>
					<Alert severity='warning'>
						{ENDORSEMENT_NEW_POLICY_END_DATE_WARNING_MSG()}
					</Alert>
				</Grid>
			)}
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Grid item xs={6}>
					<DateField<FormValues>
						disableTimePicker
						authData={authData}
						control={control}
						disabled={!isEditable}
						showCalendar
						minimum={minimumEffectiveDateString}
						maximum={maximumEffectiveDateString}
						name='effectiveDate'
						title={`${t('endorsement.singular.upper')} Effective Date`}
						precision={
							DATE_PICKER_PRECISION as 'second' | 'day' | 'month' | 'year'
						}
						type='date'
						values={[]}
						order={0}
					/>
				</Grid>

				<Grid item xs={6}>
					{(isEndorsementEndDateEnabled || !!newPolicyEndTimestamp) && (
						<DateField<FormValues>
							disableTimePicker
							authData={authData}
							control={control}
							disabled={!isEditable || !isEndorsementEndDateEnabled}
							optional
							showCalendar
							defaultCalendarMonth={
								currEffectiveDayJsObj.isValid()
									? currEffectiveDayJsObj.add(1, 'year')
									: null
							}
							minimum={
								currEffectiveDateTimeString ? currEffectiveDateTimeString : null
							}
							name='newPolicyEndDate'
							title='New Policy End Date'
							helperText={helperText}
							precision={
								DATE_PICKER_PRECISION as 'second' | 'day' | 'month' | 'year'
							}
							type='date'
							values={[]}
							order={0}
						/>
					)}
				</Grid>
			</LocalizationProvider>
			{paymentScheduleOptions.length > 0 && hasPaymentPlanPluginEnabled && (
				<Grid item xs={6} key={'newPaymentSchedule'}>
					<FormControl style={{ flexGrow: 1 }} fullWidth>
						<FieldLabel title={'New Payment Schedule'} />
						<ReactSelect
							id='payment-schedule-selector'
							value={defaultPaymentSchedule}
							options={paymentScheduleOptions}
							onChange={updatePaymentSchedule}
							isDisabled={endorsementUpdateObj.state !== 'application'}
						/>
					</FormControl>
				</Grid>
			)}
			{repriceBehavior === 'input' && (
				<Grid item xs={6} className={classes.grid} key={'reprice'}>
					<FormControl>
						<FieldLabel title={'Reprice'} required />
						<ReactSelect
							id='reprice-selector'
							value={{
								value: repriceBool.toString(),
								label: repriceBool.toString() === 'true' ? 'Yes' : 'No',
							}}
							options={[
								{ value: 'true', label: 'Yes' },
								{ value: 'false', label: 'No' },
							]}
							onChange={updateReprice}
							isDisabled={endorsementUpdateObj.state !== 'application'}
						/>
					</FormControl>
				</Grid>
			)}
			<FastForm
				formName={FORM_NAME.ENDORSEMENT_FORM}
				form={form}
				configVersion={productConfig.configVersion}
				authData={authData}
				productName={productConfig.name}
				disabled={!isEditable}
				setLoading={setLoading}
				save={saveFastForm}
				effectiveDate={endorsementEffectiveTimestamp}
				disableExternalCalls
			/>
		</Grid>
	);
};

export default PolicyDetails;
